import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import '../App.css';

function GameHistory({ onGameSelect, onNavigateToReview, onStartNewGame }) {
  const { user, userProfile } = useAuth();
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGame, setSelectedGame] = useState(null);
  const [gameDetails, setGameDetails] = useState(null);
  const [schemaCacheError, setSchemaCacheError] = useState(false);

  useEffect(() => {
    if (user) {
      loadGames();
    }
  }, [user]);

  const loadGames = async () => {
    if (!user) return;
    
    try {
      console.log('=== GameHistory: Loading completed games from database ===');
      console.log('User role:', userProfile?.role);
      
      let gamesQuery;
      
      if (userProfile?.role === 'host') {
        // For hosts: Load games they created that have submissions
        gamesQuery = supabase
          .from('games')
          .select(`
            id,
            game_name,
            game_date,
            created_at,
            updated_at,
            submissions(count)
          `)
          .eq('created_by', user.id)
          .order('created_at', { ascending: false });
      } else {
        // For players: Load games they participated in via game_participants
        // Handle both authenticated participants (user_id) and email-only participants (email)
        const userEmail = user.email?.toLowerCase();
        
        // For players: Check both user_id (authenticated) and email (email-only participants)
        const { data: participantsById, error: participantsByIdError } = user.id 
          ? await supabase
              .from('game_participants')
              .select('game_id')
              .eq('user_id', user.id)
          : { data: null, error: null };
        
        const { data: participantsByEmail, error: participantsByEmailError } = userEmail
          ? await supabase
              .from('game_participants')
              .select('game_id')
              .eq('email', userEmail)
          : { data: null, error: null };
        
        if (participantsByIdError || participantsByEmailError) {
          console.error('Error loading game participants:', participantsByIdError || participantsByEmailError);
          setGames([]);
          setLoading(false);
          return;
        }
        
        // Combine and deduplicate game IDs
        const allGameIds = new Set();
        if (participantsById) {
          participantsById.forEach(p => allGameIds.add(p.game_id));
        }
        if (participantsByEmail) {
          participantsByEmail.forEach(p => allGameIds.add(p.game_id));
        }
        
        const gameIds = Array.from(allGameIds);
        
        if (gameIds.length === 0) {
          console.log('No games participated in');
          setGames([]);
          setLoading(false);
          return;
        }
        
        console.log('Player participated in games:', gameIds);
        
        // Then load those games that have submissions
        gamesQuery = supabase
          .from('games')
          .select(`
            id,
            game_name,
            game_date,
            created_at,
            updated_at,
            submissions(count)
          `)
          .in('id', gameIds)
          .order('created_at', { ascending: false });
      }
      
      const { data: games, error } = await gamesQuery;

      console.log('=== GameHistory: Query result ===');
      console.log('Games data:', games);
      console.log('Error:', error);
      console.log('Error code:', error?.code);
      console.log('Error message:', error?.message);

      if (error) {
        // Check if it's a schema cache error - use EXACT same handling as App.js
        if (error.code === 'PGRST205') {
          setSchemaCacheError(true);
          console.log('Schema cache error detected. This is temporary - the table exists but PostgREST needs to refresh.');
          console.log('Waiting 5 seconds and retrying...');
          
          // Wait longer for cache to refresh (same as App.js)
          await new Promise(resolve => setTimeout(resolve, 5000));
          
          // Retry with same filtering logic
          let retryQuery;
          if (userProfile?.role === 'host') {
            retryQuery = supabase
              .from('games')
              .select(`
                id,
                game_name,
                game_date,
                created_at,
                updated_at,
                submissions(count)
              `)
              .eq('created_by', user.id)
              .order('created_at', { ascending: false });
          } else {
            // For players: get game IDs they participated in (by user_id OR email)
            const userEmail = user.email?.toLowerCase();
            
            const { data: retryParticipantsById } = user.id
              ? await supabase
                  .from('game_participants')
                  .select('game_id')
                  .eq('user_id', user.id)
              : { data: null };
            
            const { data: retryParticipantsByEmail } = userEmail
              ? await supabase
                  .from('game_participants')
                  .select('game_id')
                  .eq('email', userEmail)
              : { data: null };
            
            // Combine and deduplicate
            const retryGameIdsSet = new Set();
            if (retryParticipantsById) {
              retryParticipantsById.forEach(p => retryGameIdsSet.add(p.game_id));
            }
            if (retryParticipantsByEmail) {
              retryParticipantsByEmail.forEach(p => retryGameIdsSet.add(p.game_id));
            }
            
            const retryGameIds = Array.from(retryGameIdsSet);
            
            if (retryGameIds.length === 0) {
              setGames([]);
              setLoading(false);
              return;
            }
            
            retryQuery = supabase
              .from('games')
              .select(`
                id,
                game_name,
                game_date,
                created_at,
                updated_at,
                submissions(count)
              `)
              .in('id', retryGameIds)
              .order('created_at', { ascending: false });
          }
          
          const { data: retryGames, error: retryError } = await retryQuery;
          
          console.log('=== GameHistory: Retry result ===');
          console.log('Retry games:', retryGames);
          console.log('Retry error:', retryError);
          
          if (retryError) {
            console.error('Retry also failed:', retryError);
            console.log('The games table exists but PostgREST API cache hasn\'t refreshed yet.');
            console.log('Please wait 2-5 minutes and refresh the page.');
            setSchemaCacheError(true);
            setGames([]);
            return;
          }
          
          if (retryGames && retryGames.length > 0) {
            console.log(`✅ Successfully loaded ${retryGames.length} games after retry`);
            setSchemaCacheError(false);
            // Filter to only games with submissions (completed games)
            const completedGames = retryGames.filter(game => {
              // submissions(count) returns an array with count info
              const submissionCount = game.submissions?.[0]?.count || 0;
              return submissionCount > 0;
            });
            // Sort by game_date if available, otherwise created_at
            const sortedData = [...completedGames].sort((a, b) => {
              const dateA = a.game_date || a.created_at;
              const dateB = b.game_date || b.created_at;
              return new Date(dateB) - new Date(dateA);
            });
            setGames(sortedData);
            return;
          }
          
          // Retry worked but no games exist
          console.log('⚠️ Retry succeeded but no games found in database');
          setSchemaCacheError(false);
          setGames([]);
          return;
        }
        
        // Non-cache error
        console.error('❌ Non-cache error loading games:', error);
        console.error('Full error details:', JSON.stringify(error, null, 2));
        setGames([]);
        return;
      }

      // Success on first try
      if (games && games.length > 0) {
        console.log(`✅ Successfully loaded ${games.length} games on first try`);
        console.log('Games:', games);
        setSchemaCacheError(false);
        // Filter to only games with submissions (completed games)
        const completedGames = games.filter(game => {
          // submissions(count) returns an array with count info
          const submissionCount = game.submissions?.[0]?.count || 0;
          return submissionCount > 0;
        });
        console.log(`Filtered to ${completedGames.length} completed games (with submissions)`);
        console.log('Completed games with dates:', completedGames.map(g => ({
          name: g.game_name,
          game_date: g.game_date,
          created_at: g.created_at
        })));
        // Sort by game_date if available, otherwise created_at
        const sortedData = [...completedGames].sort((a, b) => {
          const dateA = a.game_date || a.created_at;
          const dateB = b.game_date || b.created_at;
          return new Date(dateB) - new Date(dateA);
        });
        setGames(sortedData);
      } else {
        console.log('⚠️ No games found in database (query succeeded but returned empty array)');
        setSchemaCacheError(false);
        setGames([]);
      }
    } catch (error) {
      console.error('❌ Exception loading games:', error);
      console.error('Full exception:', JSON.stringify(error, null, 2));
      setGames([]);
    } finally {
      setLoading(false);
    }
  };

  const loadGameDetails = async (gameId) => {
    try {
      // Load submissions
      const { data: submissions, error: submissionsError } = await supabase
        .from('submissions')
        .select('*')
        .eq('game_id', gameId)
        .order('created_at', { ascending: false });

      if (submissionsError) {
        console.error('Error loading submissions:', submissionsError);
        return;
      }

      // Load scores
      const submissionIds = submissions?.map(s => s.id) || [];
      let scores = [];
      if (submissionIds.length > 0) {
        const { data: scoresData, error: scoresError } = await supabase
          .from('scores')
          .select('*')
          .in('submission_id', submissionIds);

        if (!scoresError && scoresData) {
          scores = scoresData;
        }
      }

      // Load resources
      const { data: resources, error: resourcesError } = await supabase
        .from('resources')
        .select('*')
        .eq('game_id', gameId)
        .order('created_at', { ascending: false });

      // Load game details
      const { data: details, error: detailsError } = await supabase
        .from('game_details')
        .select('*')
        .eq('game_id', gameId)
        .limit(1)
        .single();

      setGameDetails({
        submissions: submissions || [],
        scores: scores || [],
        resources: resources || [],
        gameDetails: details || null
      });
    } catch (error) {
      console.error('Error loading game details:', error);
    }
  };

  const handleGameClick = async (game) => {
    setSelectedGame(game);
    await loadGameDetails(game.id);
  };

  const formatDate = (dateString) => {
    if (!dateString) {
      console.warn('formatDate called with no dateString');
      return 'Unknown date';
    }
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        console.warn('Invalid date:', dateString);
        return 'Invalid date';
      }
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error, dateString);
      return 'Invalid date';
    }
  };

  if (loading) {
    return (
      <div className="section">
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <p>Loading game history...</p>
        </div>
      </div>
    );
  }

  if (selectedGame && gameDetails) {
    // Show game details view
    const uniqueDesigners = new Set(gameDetails.submissions.map(s => s.designer_name));
    
    // Calculate scores for each submission
    const submissionsWithScores = gameDetails.submissions.map(submission => {
      const submissionScores = {};
      gameDetails.scores
        .filter(score => score.submission_id === submission.id)
        .forEach(score => {
          submissionScores[score.requirement_id] = score.score;
        });
      
      const totalScore = Object.values(submissionScores).reduce((sum, score) => sum + (parseInt(score) || 0), 0);
      
      return {
        ...submission,
        scores: submissionScores,
        totalScore
      };
    });

    // Filter submissions by session (with scores already calculated)
    const sessionASubmissions = submissionsWithScores.filter(s => s.session_name === 'sessionA');
    const sessionBSubmissions = submissionsWithScores.filter(s => s.session_name === 'sessionB');

    return (
      <div className="section">
        <div className="section-header">
          <div>
            <h2 style={{ margin: 0, color: '#ffffff', fontSize: '1.5rem', fontWeight: 600 }}>
              {selectedGame.game_name || 'Untitled Game'}
            </h2>
            <p style={{ color: 'rgba(255, 255, 255, 0.6)', margin: '0.5rem 0 0 0', fontSize: '1rem', fontWeight: 500 }}>
              {formatDate(selectedGame.game_date || selectedGame.created_at)}
            </p>
          </div>
        </div>

        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ color: '#ffffff', marginBottom: '1rem' }}>Game Summary</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginTop: '1rem' }}>
            <div style={{ padding: '1rem', background: 'rgba(255, 255, 255, 0.05)', borderRadius: '8px', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
              <div style={{ fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '0.5rem' }}>Designers</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#ffffff' }}>
                {uniqueDesigners.size}
              </div>
            </div>
            <div style={{ padding: '1rem', background: 'rgba(255, 255, 255, 0.05)', borderRadius: '8px', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
              <div style={{ fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '0.5rem' }}>Session A Submissions</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#ffffff' }}>
                {sessionASubmissions.length}
              </div>
            </div>
            <div style={{ padding: '1rem', background: 'rgba(255, 255, 255, 0.05)', borderRadius: '8px', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
              <div style={{ fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '0.5rem' }}>Session B Submissions</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#ffffff' }}>
                {sessionBSubmissions.length}
              </div>
            </div>
          </div>
        </div>

        {gameDetails.gameDetails && (
          <div style={{ marginBottom: '2rem', padding: '1.5rem', background: 'rgba(255, 255, 255, 0.05)', borderRadius: '8px', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <h3 style={{ color: '#ffffff', marginBottom: '1rem' }}>Game Details</h3>
            {gameDetails.gameDetails.instructions && (
              <div style={{ marginTop: '0.5rem', color: 'rgba(255, 255, 255, 0.8)' }}>
                <strong style={{ color: '#ffffff' }}>Instructions:</strong> {gameDetails.gameDetails.instructions}
              </div>
            )}
            {gameDetails.gameDetails.timing && (
              <div style={{ marginTop: '0.5rem', color: 'rgba(255, 255, 255, 0.8)' }}>
                <strong style={{ color: '#ffffff' }}>Timing:</strong> {gameDetails.gameDetails.timing}
              </div>
            )}
          </div>
        )}

        {gameDetails.resources.length > 0 && (
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ color: '#ffffff', marginBottom: '1rem' }}>Resources ({gameDetails.resources.length})</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
              {gameDetails.resources.map(resource => (
                <span key={resource.id} style={{ padding: '0.5rem 1rem', background: 'rgba(215, 2, 246, 0.2)', border: '1px solid rgba(215, 2, 246, 0.3)', borderRadius: '6px', fontSize: '0.9rem', color: '#ffffff' }}>
                  {resource.name}
                </span>
              ))}
            </div>
          </div>
        )}

        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ color: '#ffffff', marginBottom: '1rem' }}>All Submissions</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '1rem', marginTop: '1rem' }}>
            {submissionsWithScores.map(submission => (
              <div key={submission.id} style={{ padding: '1rem', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '0.5rem', color: '#ffffff' }}>{submission.designer_name}</div>
                <div style={{ fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '0.5rem' }}>
                  {submission.session_name === 'sessionA' ? 'Session A' : 'Session B'}
                </div>
                <img 
                  src={submission.image_url} 
                  alt={`${submission.designer_name}'s submission`}
                  style={{ width: '100%', height: '150px', objectFit: 'contain', marginBottom: '0.5rem', background: 'rgba(255, 255, 255, 0.02)', borderRadius: '6px' }}
                />
                <div style={{ fontSize: '0.9rem', color: '#D702F6', fontWeight: 'bold' }}>
                  Score: {submission.totalScore} pts
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Session A Leaderboard */}
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ color: '#ffffff', marginBottom: '1rem' }}>Leaderboard - Session A</h3>
          {sessionASubmissions.length === 0 ? (
            <p style={{ color: 'rgba(255, 255, 255, 0.5)', fontStyle: 'italic', marginTop: '0.5rem' }}>
              No submissions for Session A yet.
            </p>
          ) : (
            <div style={{ marginTop: '1rem' }}>
              {[...sessionASubmissions]
                .sort((a, b) => b.totalScore - a.totalScore)
                .map((submission, index) => (
                  <div 
                    key={submission.id}
                    style={{
                      padding: '1rem',
                      marginBottom: '0.5rem',
                      background: index === 0 ? 'rgba(255, 193, 7, 0.15)' : 'rgba(255, 255, 255, 0.05)',
                      borderRadius: '8px',
                      border: index === 0 ? '2px solid rgba(255, 193, 7, 0.5)' : '1px solid rgba(255, 255, 255, 0.1)',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <span style={{
                        fontSize: '1.5rem',
                        fontWeight: 'bold',
                        color: index === 0 ? '#FFC107' : 'rgba(255, 255, 255, 0.6)',
                        minWidth: '30px'
                      }}>
                        #{index + 1}
                      </span>
                      <div>
                        <strong style={{ color: '#ffffff' }}>{submission.designer_name}</strong>
                        <div style={{ fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.6)', marginTop: '0.25rem' }}>
                          {Object.values(submission.scores || {}).filter(s => s > 0).length} reqs
                        </div>
                      </div>
                    </div>
                    <div style={{
                      fontSize: '1.5rem',
                      fontWeight: 'bold',
                      color: index === 0 ? '#FFC107' : '#D702F6'
                    }}>
                      {submission.totalScore} pts
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>

        {/* Session B Leaderboard */}
        <div>
          <h3 style={{ color: '#ffffff', marginBottom: '1rem' }}>Leaderboard - Session B</h3>
          {sessionBSubmissions.length === 0 ? (
            <p style={{ color: 'rgba(255, 255, 255, 0.5)', fontStyle: 'italic', marginTop: '0.5rem' }}>
              No submissions for Session B yet.
            </p>
          ) : (
            <div style={{ marginTop: '1rem' }}>
              {[...sessionBSubmissions]
                .sort((a, b) => b.totalScore - a.totalScore)
                .map((submission, index) => (
                  <div 
                    key={submission.id}
                    style={{
                      padding: '1rem',
                      marginBottom: '0.5rem',
                      background: index === 0 ? 'rgba(255, 193, 7, 0.15)' : 'rgba(255, 255, 255, 0.05)',
                      borderRadius: '8px',
                      border: index === 0 ? '2px solid rgba(255, 193, 7, 0.5)' : '1px solid rgba(255, 255, 255, 0.1)',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <span style={{
                        fontSize: '1.5rem',
                        fontWeight: 'bold',
                        color: index === 0 ? '#FFC107' : 'rgba(255, 255, 255, 0.6)',
                        minWidth: '30px'
                      }}>
                        #{index + 1}
                      </span>
                      <div>
                        <strong style={{ color: '#ffffff' }}>{submission.designer_name}</strong>
                        <div style={{ fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.6)', marginTop: '0.25rem' }}>
                          {Object.values(submission.scores || {}).filter(s => s > 0).length} reqs
                        </div>
                      </div>
                    </div>
                    <div style={{
                      fontSize: '1.5rem',
                      fontWeight: 'bold',
                      color: index === 0 ? '#FFC107' : '#D702F6'
                    }}>
                      {submission.totalScore} pts
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="section" style={{ maxWidth: '1000px', width: '90%', margin: '0 auto' }}>

      {games.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: 'rgba(255, 255, 255, 0.7)' }}>
          {schemaCacheError ? (
            <>
              <p style={{ marginBottom: '1rem', color: '#d97706', fontWeight: 'bold' }}>
                ⚠️ Schema Cache Issue Detected
              </p>
              <p style={{ marginBottom: '1rem', color: 'rgba(255, 255, 255, 0.9)' }}>
                The games table exists in your database, but Supabase's API cache hasn't refreshed yet.
              </p>
              <p style={{ fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.7)', marginBottom: '1rem' }}>
                <strong>What to do:</strong>
              </p>
              <ol style={{ textAlign: 'left', display: 'inline-block', fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.7)', marginBottom: '1rem' }}>
                <li>Wait 5-15 minutes for the cache to refresh automatically</li>
                <li>Click the "Refresh" button above periodically</li>
                <li>Or try refreshing your browser (Cmd+Shift+R / Ctrl+Shift+R)</li>
              </ol>
              <p style={{ fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.5)', fontStyle: 'italic' }}>
                If you played games before the game history feature was added, run the "link_old_submissions.sql" script in Supabase SQL Editor.
              </p>
            </>
          ) : (
            <>
              <p style={{ marginBottom: '1rem', color: 'rgba(255, 255, 255, 0.9)' }}>
                {userProfile?.role === 'host' 
                  ? "You haven't created any games yet." 
                  : "You haven't joined any games yet."}
              </p>
              <p style={{ fontSize: '0.875rem', marginTop: '0.5rem', marginBottom: '1.5rem', color: 'rgba(255, 255, 255, 0.5)' }}>
                {userProfile?.role === 'host' 
                  ? 'Create your first game to get started!' 
                  : 'Join a game using a join code to get started!'}
              </p>
              {onStartNewGame && userProfile?.role === 'host' && (
                <button
                  onClick={onStartNewGame}
                  style={{
                    padding: '0.75rem 2rem',
                    background: '#D702F6',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#ffffff',
                    fontSize: '1rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 4px 15px rgba(215, 2, 246, 0.4)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#C001E5';
                    e.target.style.boxShadow = '0 6px 20px rgba(215, 2, 246, 0.6)';
                    e.target.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = '#D702F6';
                    e.target.style.boxShadow = '0 4px 15px rgba(215, 2, 246, 0.4)';
                    e.target.style.transform = 'translateY(0)';
                  }}
                >
                  Start New Game
                </button>
              )}
            </>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {games.map(game => (
            <div
              key={game.id}
              onClick={() => onGameSelect(game)}
              style={{
                padding: '1.5rem',
                background: 'rgba(255, 255, 255, 0.05)',
                border: '2px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#D702F6';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(215, 2, 246, 0.3)';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ flex: 1 }}>
                  <h3 style={{ margin: 0, color: '#ffffff', fontSize: '1.1rem', fontWeight: 600 }}>
                    {game.game_name || 'Untitled Game'}
                  </h3>
                  <p style={{ margin: '0.5rem 0 0 0', color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.95rem', fontWeight: 500 }}>
                    {(() => {
                      const dateToFormat = game.game_date || game.created_at;
                      console.log('Formatting date for game:', game.game_name, 'date:', dateToFormat);
                      return formatDate(dateToFormat);
                    })()}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {onNavigateToReview && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onNavigateToReview(game);
                      }}
                      style={{ 
                        backgroundColor: '#D702F6',
                        border: '2px solid #D702F6',
                        color: '#ffffff',
                        padding: '0.5rem 1rem',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        transition: 'background-color 0.3s ease, border-color 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#C001E5';
                        e.target.style.borderColor = '#C001E5';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = '#D702F6';
                        e.target.style.borderColor = '#D702F6';
                      }}
                    >
                      View Scores
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default GameHistory;

